import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { MONTHS_PT, WEEKDAYS_PT, buildMonthGrid, isoForDay } from '@components/dateInputUtils';
import { usePermissions } from '@hooks/usePermissions';
import { useSelectedSocialAccount } from '@hooks/useSelectedSocialAccount';
import { useSocialPost } from '@contexts/SocialPostContext';
import socialService from '@services/socialService';
import type { Network, Pillar, PostStatus, SocialPost } from '@/types/social';
import { NETWORKS, NETWORK_LABELS, POST_STATUS_LABELS } from '@/types/social';
import { addDaysISO, formatDateBR, monthRangeISO, todayISO, weekdayISO } from '@/utils/formatters';
import { STATUS_CHIP, groupPostsByDay, isLocked, moveToDay, timeOf } from '@/utils/social';
import { shiftMonth } from '@pages/calendar/calendarUtils';
import { ApprovedSeal, PostCover, PostNetworks, StatusChip } from '@components/social/SocialBits';
import { ProfileSwitcher } from '@components/social/ProfileSwitcher';
import { PillarBalancePanel } from './SocialGridView';

type View = 'month' | 'week';

/**
 * Calendário de posts: mês ou semana, todas as redes, filtros por perfil/rede/status/pilar. Arrastar
 * um post para outro dia reagenda mantendo a hora; arrastar um "sem data" para um dia agenda às
 * 09:00. Documentação: src/docs/redes-sociais/calendario.md.
 */
export const SocialCalendarView: React.FC = () => {
  const now = new Date();
  // No celular a grade do mês fica apertada: começa na semana (um dia embaixo do outro).
  const [view, setView] = useState<View>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'week' : 'month'));
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [weekStart, setWeekStart] = useState(() => addDaysISO(todayISO(), -weekdayISO(todayISO())));
  const [network, setNetwork] = useState<Network | ''>('');
  const [status, setStatus] = useState<PostStatus | ''>('');
  const [pillarId, setPillarId] = useState('');
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [unscheduled, setUnscheduled] = useState<SocialPost[]>([]);
  const [balance, setBalance] = useState<Awaited<ReturnType<typeof socialService.balance>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [overDay, setOverDay] = useState<string | null>(null);
  const { openPost, openNewPost, version } = useSocialPost();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('social.write').hasPermission;
  const today = todayISO();
  const { accounts, account, accountId, setAccountId, ready } = useSelectedSocialAccount();
  // Com um perfil escolhido, a rede é a dele.
  const effNetwork: Network | '' = account ? account.network : network;
  const accountPreset = account ? { account: { id: account.id, network: account.network } } : {};

  const range = useMemo(
    () => (view === 'month' ? monthRangeISO(year, month) : { start: weekStart, end: addDaysISO(weekStart, 6) }),
    [view, year, month, weekStart],
  );

  useEffect(() => {
    socialService.listPillars().then(setPillars).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    const f = {
      network: effNetwork,
      account_id: accountId ?? undefined,
      status: status ? [status] : undefined,
      pillar_id: pillarId ? Number(pillarId) : undefined,
    };
    try {
      const [inRange, noDate, bal] = await Promise.all([
        socialService.listPosts({ ...f, from: range.start, to: range.end }),
        socialService.listPosts({ ...f, unscheduled: true }),
        socialService.balance(range.start, range.end),
      ]);
      setPosts(inRange);
      setUnscheduled(noDate);
      setBalance(bal);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [effNetwork, accountId, ready, status, pillarId, range]);

  useEffect(() => {
    load();
  }, [load, version]);

  const byDay = useMemo(() => groupPostsByDay(posts), [posts]);

  const move = (delta: number) => {
    if (view === 'month') {
      const n = shiftMonth(year, month, delta);
      setYear(n.year);
      setMonth(n.month);
    } else {
      setWeekStart((w) => addDaysISO(w, 7 * delta));
    }
  };

  const dropOn = async (day: string) => {
    const id = dragId;
    setDragId(null);
    setOverDay(null);
    if (!id) return;
    const p = [...posts, ...unscheduled].find((x) => x.id === id);
    if (!p || isLocked(p.status)) return;
    if (p.scheduled_at?.slice(0, 10) === day) return;
    try {
      await socialService.schedule(id, moveToDay(p.scheduled_at, day));
      await load();
    } catch (err) {
      setError(err);
    }
  };

  const dayProps = (iso: string) => ({
    onDragOver: (e: React.DragEvent) => {
      if (dragId) {
        e.preventDefault();
        setOverDay(iso);
      }
    },
    onDragLeave: () => setOverDay((d) => (d === iso ? null : d)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      dropOn(iso);
    },
  });

  const chip = (p: SocialPost, big = false) => (
    <button
      key={p.id}
      type="button"
      draggable={canWrite && !isLocked(p.status)}
      onDragStart={() => setDragId(p.id)}
      onDragEnd={() => setDragId(null)}
      onClick={() => openPost(p.id)}
      title={`${p.title} · ${POST_STATUS_LABELS[p.status]}`}
      className={clsx(
        'w-full text-left rounded px-1 py-0.5 text-[11px] leading-tight hover:opacity-80 flex items-center gap-1',
        STATUS_CHIP[p.status],
        dragId === p.id && 'opacity-40',
      )}
    >
      {big && <PostCover post={p} className="w-10 h-10 rounded flex-shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="font-semibold">{timeOf(p.scheduled_at)}</span>
          <PostNetworks post={p} size={10} />
          <ApprovedSeal status={p.status} />
        </span>
        <span className="block truncate">{p.title}</span>
      </span>
    </button>
  );

  const weekDays = Array.from({ length: 7 }, (_, i) => addDaysISO(weekStart, i));
  const cells = buildMonthGrid(year, month);

  return (
    <div className="space-y-4">
      <ProfileSwitcher accounts={accounts} value={accountId} onChange={setAccountId} allowAll />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => move(-1)} className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50" aria-label="Anterior">
            <ChevronLeft size={16} />
          </button>
          <span className="text-base sm:text-lg font-semibold text-slate-800 sm:min-w-[200px] text-center">
            {view === 'month' ? `${MONTHS_PT[month]} ${year}` : `${formatDateBR(range.start)} a ${formatDateBR(range.end)}`}
          </span>
          <button type="button" onClick={() => move(1)} className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50" aria-label="Próximo">
            <ChevronRight size={16} />
          </button>
          <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden sm:ml-2">
            {(['month', 'week'] as View[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={clsx('px-3 py-1.5 text-sm', view === v ? 'bg-emerald-600 text-white' : 'text-slate-600')}
              >
                {v === 'month' ? 'Mês' : 'Semana'}
              </button>
            ))}
          </div>
          {loading && <Loader2 size={16} className="animate-spin text-slate-400 ml-2" />}
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap items-center gap-2 w-full sm:w-auto">
          <select
            className="select-field w-full sm:w-36"
            value={effNetwork}
            disabled={!!account}
            title={account ? 'A rede é a do perfil escolhido' : undefined}
            onChange={(e) => setNetwork(e.target.value as Network | '')}
          >
            <option value="">Todas as redes</option>
            {NETWORKS.map((n) => (
              <option key={n} value={n}>
                {NETWORK_LABELS[n]}
              </option>
            ))}
          </select>
          <select className="select-field w-full sm:w-44" value={status} onChange={(e) => setStatus(e.target.value as PostStatus | '')}>
            <option value="">Todos os status</option>
            {Object.entries(POST_STATUS_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <select className="select-field w-full sm:w-36" value={pillarId} onChange={(e) => setPillarId(e.target.value)}>
            <option value="">Todos os pilares</option>
            {pillars.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-4">
        <div className="flex-1 min-w-0">
          {view === 'month' ? (
            <div className="overflow-x-auto">
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200 min-w-[640px]">
              {WEEKDAYS_PT.map((wd) => (
                <div key={wd} className="bg-slate-50 text-center text-xs font-medium text-slate-500 py-2">
                  {wd}
                </div>
              ))}
              {cells.map((day, idx) => {
                if (day === null) return <div key={`e-${idx}`} className="bg-slate-50/60 min-h-[110px]" />;
                const iso = isoForDay(year, month, day);
                return (
                  <div
                    key={iso}
                    {...dayProps(iso)}
                    className={clsx(
                      'group bg-white min-h-[110px] p-1.5 flex flex-col',
                      iso === today && 'ring-2 ring-inset ring-blue-400',
                      overDay === iso && 'bg-emerald-50',
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={clsx('text-xs', iso === today ? 'font-bold text-blue-600' : 'text-slate-500')}>{day}</span>
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => openNewPost({ scheduled_at: `${iso}T09:00`, ...accountPreset })}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-400 hover:text-emerald-700 hover:bg-emerald-50"
                          title={`Novo post em ${formatDateBR(iso)}`}
                        >
                          <Plus size={12} />
                        </button>
                      )}
                    </div>
                    <div className="space-y-0.5">{(byDay[iso] ?? []).map((p) => chip(p))}</div>
                  </div>
                );
              })}
            </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
              {weekDays.map((iso) => (
                <div
                  key={iso}
                  {...dayProps(iso)}
                  className={clsx(
                    'rounded-xl border bg-white p-2 md:min-h-[320px] space-y-1',
                    iso === today ? 'border-blue-400' : 'border-slate-200',
                    overDay === iso && 'bg-emerald-50',
                  )}
                >
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span className={clsx(iso === today && 'font-bold text-blue-600')}>
                      {WEEKDAYS_PT[weekdayISO(iso)]} {formatDateBR(iso).slice(0, 5)}
                    </span>
                    {canWrite && (
                      <button type="button" onClick={() => openNewPost({ scheduled_at: `${iso}T09:00`, ...accountPreset })} className="p-0.5 rounded text-slate-400 hover:text-emerald-700" title="Novo post">
                        <Plus size={12} />
                      </button>
                    )}
                  </div>
                  {(byDay[iso] ?? []).map((p) => chip(p, true))}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {(['strategy', 'draft', 'awaiting_approval', 'changes_requested', 'scheduled', 'published', 'failed'] as PostStatus[]).map((s) => (
              <StatusChip key={s} status={s} />
            ))}
          </div>
        </div>

        <aside className="xl:w-80 flex-shrink-0 space-y-4">
          <PillarBalancePanel balance={balance} title="Equilíbrio dos pilares no período" />
          <section className="card p-3 space-y-2">
            <h3 className="text-xs font-semibold uppercase text-slate-500">Sem data ({unscheduled.length})</h3>
            <p className="text-[11px] text-slate-500">Arraste para um dia do calendário para agendar às 09:00 (ajuste a hora no post).</p>
            <div className="space-y-1 max-h-[480px] overflow-y-auto custom-scrollbar">{unscheduled.map((p) => chip(p, true))}</div>
          </section>
        </aside>
      </div>
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialCalendarView;
